import type { SearchSchemaInput } from '@tanstack/react-router';
import { parseEnum, validateListSearch, type ListSearch } from '@/shared/lib/listSearch';
import {
  ContractsControllerListV1SortBy,
  type ContractsControllerListV1Params,
  type ContractsControllerListV1Quick,
  type ContractsControllerRenewalsV1Bucket,
  type ContractsControllerRenewalsV1Params,
  type ContractStatus,
  type ContractType,
} from '@/api/generated/model';
import { CONTRACT_STATUSES, CONTRACT_TYPES, QUICK_FILTERS, RENEWAL_BUCKETS } from './contractMeta';

export const CONTRACTS_TABS = ['register', 'renewals'] as const;
export type ContractsTab = (typeof CONTRACTS_TABS)[number];

export type ContractSortField = ContractsControllerListV1SortBy;

const SORT_FIELDS = [
  ContractsControllerListV1SortBy.recent,
  ContractsControllerListV1SortBy.ref,
  ContractsControllerListV1SortBy.title,
  ContractsControllerListV1SortBy.startDate,
  ContractsControllerListV1SortBy.endDate,
  ContractsControllerListV1SortBy.status,
  ContractsControllerListV1SortBy.updated,
] as const;

/**
 * URL state for `/contracts`.
 *
 * Both tabs share one route and one search object. The register's filters and
 * the renewals' bucket coexist in the URL so switching tabs loses nothing; each
 * tab reads only the keys it owns. See `players/lib/playersSearch.ts` for why
 * list state lives in the URL at all.
 */
export interface ContractsSearch extends Omit<ListSearch, 'sortBy'> {
  tab: ContractsTab;
  sortBy?: ContractSortField | undefined;
  quick: ContractsControllerListV1Quick;
  status?: ContractStatus | undefined;
  type?: ContractType | undefined;
  playerId?: string | undefined;
  bucket?: ContractsControllerRenewalsV1Bucket | undefined;
}

export function validateContractsSearch(
  search: Partial<Record<keyof ContractsSearch, unknown>> & SearchSchemaInput,
): ContractsSearch {
  const base = validateListSearch(search);
  const sortBy = parseEnum(base.sortBy, SORT_FIELDS);
  return {
    ...base,
    sortBy,
    sortOrder: sortBy ? base.sortOrder : undefined,
    tab: parseEnum(search.tab, CONTRACTS_TABS) ?? 'register',
    quick: parseEnum(search.quick, QUICK_FILTERS) ?? 'all',
    status: parseEnum(search.status, CONTRACT_STATUSES),
    type: parseEnum(search.type, CONTRACT_TYPES),
    playerId: toId(search.playerId),
    bucket: parseEnum(search.bucket, RENEWAL_BUCKETS),
  };
}

export function toContractsParams(search: ContractsSearch): ContractsControllerListV1Params {
  return {
    page: search.page,
    limit: search.limit,
    ...(search.sortBy && search.sortOrder
      ? { sortBy: search.sortBy, sortOrder: search.sortOrder }
      : {}),
    ...(search.search ? { search: search.search } : {}),
    ...(search.quick !== 'all' ? { quick: search.quick } : {}),
    ...(search.status ? { status: search.status } : {}),
    ...(search.type ? { type: search.type } : {}),
    ...(search.playerId ? { playerId: search.playerId } : {}),
  };
}

export function toRenewalsParams(search: ContractsSearch): ContractsControllerRenewalsV1Params {
  return {
    page: search.page,
    limit: search.limit,
    // Soonest first is what a renewals screen is for; the URL's sortOrder is
    // the register's and does not apply here.
    sortOrder: 'asc',
    ...(search.search ? { search: search.search } : {}),
    ...(search.bucket ? { bucket: search.bucket } : {}),
  };
}

export function hasRegisterFilters(search: ContractsSearch): boolean {
  return Boolean(
    search.search || search.quick !== 'all' || search.status || search.type || search.playerId,
  );
}

export function clearedRegisterFilters(): Partial<ContractsSearch> {
  return {
    page: 1,
    search: undefined,
    quick: 'all',
    status: undefined,
    type: undefined,
    playerId: undefined,
  };
}

function toId(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
}
